import React from 'react';
import { Helmet } from 'react-helmet-async';

/**
 * 사이트 소개 페이지 (/about)
 * 애드센스 "고유 콘텐츠/사이트 신뢰성" 요건 충족용 + SEO 랜딩.
 */

const SITE_NAME = 'CBT 편의점';
const SITE_DOMAIN = 'www.cbtstudy.com';
const SITE_CONTACT = 'support@cbtstudy.com';

function Section({ title, children }) {
  return (
    <section className="mb-8">
      <h2 className="text-xl font-bold text-gray-900 mb-3">{title}</h2>
      <div className="text-gray-700 leading-relaxed space-y-2">{children}</div>
    </section>
  );
}

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>서비스 소개 - {SITE_NAME} | 국가자격증 CBT 기출문제</title>
        <meta
          name="description"
          content={`${SITE_NAME}는 국가기술자격 CBT 기출문제를 실제 시험과 동일한 환경에서 무료로 연습할 수 있는 학습 플랫폼입니다. 600여 개 자격증의 기출문제와 해설을 제공합니다.`}
        />
        <link rel="canonical" href={`https://${SITE_DOMAIN}/about`} />
      </Helmet>

      <header className="bg-white border-b border-gray-200">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <a href="/" className="text-lg font-bold text-gray-900">{SITE_NAME}</a>
          <a href="/" className="text-sm text-orange-500 hover:text-orange-600 transition-colors">← 홈으로</a>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-10">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">서비스 소개</h1>
        <p className="text-sm text-gray-500 mb-10">{SITE_NAME} — 국가자격증 CBT 기출문제 학습 플랫폼</p>

        <p className="text-gray-700 leading-relaxed mb-8">
          {SITE_NAME}는 국가기술자격 시험을 준비하는 수험생이 실제 CBT(컴퓨터 기반 시험)와
          동일한 환경에서 기출문제를 풀어볼 수 있도록 만든 무료 학습 플랫폼입니다.
          정보처리기사, 전기기사, 산업안전기사, 건축기사 등 600여 개 종목의 기출문제와
          해설을 제공하며, 모바일에서도 끊김 없이 학습할 수 있도록 최적화했습니다.
        </p>

        <Section title="우리가 해결하려는 문제">
          <p>
            대부분의 국가기술자격 필기시험은 2020년 전후로 지필고사에서 CBT 방식으로
            전환되었습니다. 그러나 수험생이 실제 시험과 같은 화면·조작 환경에서 미리
            연습할 수 있는 곳은 많지 않습니다. {SITE_NAME}는 회차별 기출문제를 실제
            시험과 동일한 인터페이스로 제공해, 문제 풀이 감각과 시간 배분을 시험 전에
            충분히 익힐 수 있도록 돕습니다.
          </p>
        </Section>

        <Section title="주요 기능">
          <ul className="list-disc pl-5 space-y-1">
            <li><strong>회차별 기출문제 풀이</strong> — 연도·회차별 실제 출제 문제를 그대로 학습</li>
            <li><strong>상세 해설</strong> — 정답과 함께 풀이 해설을 제공해 개념 이해를 도움</li>
            <li><strong>모의고사·과목별 모드</strong> — 여러 회차를 섞어 실전처럼 연습하거나 약한 과목만 집중 학습</li>
            <li><strong>오답노트</strong> — 틀린 문제를 자동으로 모아 복습</li>
            <li><strong>학습 진도·통계</strong> — 풀이 기록과 정답률을 추적해 약점을 파악</li>
            <li><strong>수식·도식 렌더링</strong> — 공학 과목의 LaTeX 수식과 회로·구조 도식을 정확히 표시</li>
          </ul>
        </Section>

        <Section title="다루는 자격증">
          <p>
            정보처리기사·정보처리산업기사 등 IT 분야, 전기기사·전기산업기사·전기공사기사 등
            전기 분야, 산업안전기사·산업위생관리기사 등 안전 분야, 건축기사, 가스기사,
            위험물산업기사, 소방설비기사, 그리고 제과기능사·한식조리기능사·지게차운전기능사
            등 기능사까지 600여 개 국가기술자격 종목을 폭넓게 다룹니다.
          </p>
        </Section>

        <Section title="CBT 연습이 중요한 이유">
          <p>
            CBT 시험은 화면에서 문제를 읽고 마우스로 답을 선택하며, 남은 시간이 실시간으로
            표시됩니다. 종이 시험과 달리 문제 간 이동·표시 방식이 달라 처음 접하면 당황하기
            쉽습니다. 시험 전 같은 환경에서 충분히 연습하면 실수와 시간 부족을 크게 줄일 수
            있습니다. {SITE_NAME}는 이러한 실전 감각을 무료로 기를 수 있게 합니다.
          </p>
        </Section>

        <Section title="문의">
          <p>
            서비스 이용 중 궁금한 점이나 오류 제보, 제휴 문의는{' '}
            <a href={`mailto:${SITE_CONTACT}`} className="text-orange-600 underline">{SITE_CONTACT}</a>
            {' '}또는 <a href="/contact" className="text-orange-600 underline">문의 페이지</a>를 이용해 주세요.
          </p>
        </Section>

        <div className="border-t border-gray-200 mt-10 pt-6 text-sm text-gray-500 flex flex-wrap gap-4">
          <a href="/about" className="hover:text-orange-600">서비스 소개</a>
          <a href="/privacy" className="hover:text-orange-600">개인정보처리방침</a>
          <a href="/terms" className="hover:text-orange-600">이용약관</a>
          <a href="/contact" className="hover:text-orange-600">문의</a>
        </div>
      </main>
    </div>
  );
}
